import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Check } from "lucide-react";
import "@/styles/studio.css";
import "@/styles/client.css";
import { BalanceCard, ClientProgress, ConceptBoards, Documents, OrderSection, ProductBoard, Services } from "@/client/ClientV1";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/client`;
const usd = (c) => `$${((c || 0) / 100).toLocaleString("en-US", { minimumFractionDigits: (c || 0) % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;
const dollars = (v) => usd(Math.round((v || 0) * 100));
const range = (lo, hi) => (lo == null ? "To be set" : hi == null || hi === lo ? dollars(lo) : `${dollars(lo)}–${dollars(hi)}`);
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "");
const call = async (path, opts = {}) => {
  const r = await fetch(`${API}${path}`, { ...opts, headers: { "Content-Type": "application/json", ...(opts.headers || {}) } });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Something went wrong");
  return data;
};
const img = (u) => (u?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const Sec = ({ eyebrow, title, children, testId }) => (
  <section className="cp-sec" data-testid={testId}><p className="cp-eb">{eyebrow}</p><h2 className="cp-h2">{title}</h2>{children}</section>
);
const Row = ({ k, v, strong }) => <div className={`cp-row ${strong ? "is-strong" : ""}`}><span>{k}</span><span>{v}</span></div>;

const Milestones = ({ ms, note }) => (
  <div className="cp-card" data-testid="client-milestones">
    <Row k="Initial deposit · on signing (non-refundable, credited to your fee)" v={usd(ms.deposit)} />
    <Row k="Concept payment · when your concept boards are approved (brings you to 50%)" v={usd(ms.concept)} />
    <Row k="Final balance · before the shopping list or any orders" v={usd(ms.final)} />
    <Row k="Total design fee" v={usd(ms.total)} strong />
    <p className="cp-small">{note}</p>
  </div>
);

const Recap = ({ r }) => (
  <Sec eyebrow={`Proposal · v${r.version}`} title="Your design proposal" testId="client-recap">
    {r.intro && <p className="cp-lead">{r.intro}</p>}
    {r.rooms && <div className="cp-card">{r.rooms.map((x) => <Row key={x.label} k={x.label} v={[x.purpose, x.goals.join(", ")].filter(Boolean).join(" · ") || "—"} />)}</div>}
    {r.styles && (
      <div className="cp-styles">
        {r.styles.map((s) => <figure key={s.name}><img src={img(s.image_url)} alt={s.name} loading="lazy" /><figcaption>{s.name}</figcaption></figure>)}
        {r.palettes.map((p) => <div key={p.name} className="cp-pal"><div>{p.swatches.map((c) => <i key={c} style={{ background: c }} />)}</div>{p.name}</div>)}
        {r.deal_breakers?.length > 0 && <p className="cp-small">Rather not see: {r.deal_breakers.join(", ")}</p>}
      </div>
    )}
    {r.budget && (
      <div className="cp-card"><p className="cp-label">Furniture budget</p>
        {r.budget.rooms.map((b) => <Row key={b.label} k={`${b.label} · recommended ${range(b.rec_min, b.rec_max)}`} v={range(b.min, b.max)} />)}
        <Row k="Combined" v={range(r.budget.total.min, r.budget.total.max)} strong />
        <p className="cp-small">Furniture budgets exclude design fees, delivery, and assembly.</p></div>
    )}
    {r.timeline && <div className="cp-card"><Row k="Design timeline" v={r.timeline.design_weeks ? `${r.timeline.design_weeks} weeks` : "To be agreed"} /><Row k="Furniture delivery window" v={r.timeline.delivery_weeks ? `${r.timeline.delivery_weeks} weeks` : "To be agreed"} /><p className="cp-small">Delivery estimates depend on vendor availability.</p></div>}
    {r.services?.length > 0 && <p className="cp-p">Optional services you're interested in: {r.services.join(", ")} (quoted separately).</p>}
    {r.outstanding?.length > 0 && <div className="cp-card"><p className="cp-label">Still to confirm</p>{r.outstanding.map((o) => <p key={o} className="cp-p">· {o}</p>)}</div>}
    {r.next_steps && <div className="cp-card cp-card--sage"><p className="cp-label">Next steps</p><p className="cp-p">{r.next_steps}</p></div>}
  </Sec>
);

const SignaturePad = ({ onChange }) => {
  const ref = useRef(null);
  const strokes = useRef([]);
  const active = useRef(null);
  const [inked, setInked] = useState(false);
  const paint = useCallback(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const w = c.width / (window.devicePixelRatio || 1);
    const h = c.height / (window.devicePixelRatio || 1);
    ctx.clearRect(0, 0, w, h);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#2e2e2c";
    strokes.current.forEach((pts) => {
      ctx.beginPath();
      ctx.moveTo(pts[0][0] * w, pts[0][1] * h);
      if (pts.length === 1) ctx.lineTo(pts[0][0] * w + 0.1, pts[0][1] * h);
      for (let i = 1; i < pts.length - 1; i += 1) {
        const mx = ((pts[i][0] + pts[i + 1][0]) / 2) * w;
        const my = ((pts[i][1] + pts[i + 1][1]) / 2) * h;
        ctx.quadraticCurveTo(pts[i][0] * w, pts[i][1] * h, mx, my);
      }
      if (pts.length > 1) ctx.lineTo(pts[pts.length - 1][0] * w, pts[pts.length - 1][1] * h);
      ctx.stroke();
    });
  }, []);
  useEffect(() => {
    const c = ref.current;
    const fit = () => {
      const ratio = window.devicePixelRatio || 1;
      if (c.width === Math.round(c.offsetWidth * ratio)) return;
      c.width = Math.round(c.offsetWidth * ratio);
      c.height = Math.round(c.offsetHeight * ratio);
      c.getContext("2d").setTransform(ratio, 0, 0, ratio, 0, 0);
      paint();
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(c);
    return () => ro.disconnect();
  }, [paint]);
  const pt = (e) => { const b = ref.current.getBoundingClientRect(); return [(e.clientX - b.left) / b.width, (e.clientY - b.top) / b.height]; };
  const down = (e) => { e.preventDefault(); try { ref.current.setPointerCapture(e.pointerId); } catch { /* synthetic or already released pointer */ } active.current = [pt(e)]; strokes.current.push(active.current); paint(); };
  const move = (e) => {
    if (!active.current) return;
    const list = e.nativeEvent.getCoalescedEvents ? e.nativeEvent.getCoalescedEvents() : [];
    (list.length ? list : [e]).forEach((ev) => active.current.push(pt(ev)));
    paint();
  };
  const up = () => {
    if (!active.current) return;
    active.current = null;
    setInked(true);
    onChange(ref.current.toDataURL("image/png"));
  };
  const clear = () => { strokes.current = []; paint(); setInked(false); onChange(null); };
  return (
    <div className="cp-sigwrap">
      <canvas ref={ref} className="cp-sig" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} aria-label="Signature pad" data-testid="signature-canvas" />
      {!inked && <span className="cp-sig__hint">Sign here with your finger or mouse</span>}
      <span className="cp-sig__line" />
      <div className="cp-sig__bar">
        <span className="cp-small" style={{ margin: 0 }} data-testid="signature-state">{inked ? "Signature added" : "No signature yet"}</span>
        <button type="button" className="cp-btn cp-btn--ghost cp-btn--sm" onClick={clear} disabled={!inked} data-testid="signature-clear">Clear</button>
      </div>
    </div>
  );
};

const Agreement = ({ a, token, clientName, onSigned }) => {
  const s = a.snapshot;
  const [name, setName] = useState("");
  const [png, setPng] = useState(null);
  const [terms, setTerms] = useState(false);
  const [dep, setDep] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const needDep = a.kind === "original";
  const ready = name.trim().length > 1 && png && terms && (!needDep || dep);
  const sign = async () => {
    setBusy(true); setError("");
    try { await call(`/${token}/agreements/${a.id}/sign`, { method: "POST", body: JSON.stringify({ full_name: name, signature_png: png, agree_terms: terms, authorize_deposit: dep }) }); onSigned(); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  return (
    <Sec eyebrow={`Agreement ${a.number}${a.kind === "amendment" ? ` · amends ${a.amends_number}` : ""}`} title={a.kind === "amendment" ? "Agreement amendment" : "Your design agreement"} testId="client-agreement">
      <div className="cp-card">
        {s.rooms.map((r) => <Row key={r.label} k={r.label} v={dollars(r.fee)} />)}
        <Row k="Subtotal" v={dollars(s.pricing.subtotal)} />
        {s.pricing.discount && <Row k={s.pricing.discount.label} v={`−${dollars(s.pricing.discount.amount)}`} />}
        {s.pricing.self_order && <Row k={s.pricing.self_order.label} v={`−${dollars(s.pricing.self_order.amount)}`} />}
        <Row k="Total design fee" v={dollars(s.pricing.total)} strong />
      </div>
      <Milestones ms={s.milestones} note={s.final_balance_note} />
      {s.outstanding.length > 0 && <div className="cp-card"><p className="cp-label">Outstanding items</p>{s.outstanding.map((o) => <p key={o} className="cp-p">· {o}</p>)}</div>}
      <details className="cp-terms" open={a.status === "draft"} data-testid="client-terms"><summary>Terms (v{s.terms.version})</summary>{s.terms.text.split("\n\n").map((p) => <p key={p.slice(0, 30)}>{p}</p>)}</details>
      {a.status === "signed" ? (
        <div className="cp-card cp-card--sage" data-testid="client-signed"><Check size={16} /> Signed by {a.signature.full_name} on {fmt(a.signature.signed_at)}.<p className="cp-small" style={{ wordBreak: "break-all" }}>Record fingerprint (SHA-256): {a.snapshot_hash}</p></div>
      ) : (
        <div className="cp-sign" data-testid="client-sign-form">
          <label className="cp-field"><span>Type your full name ({clientName})</span><input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ready && sign()} autoComplete="name" autoCapitalize="words" enterKeyHint="done" data-testid="sign-name" /></label>
          <span className="cp-fieldlabel">Draw your signature</span>
          <SignaturePad onChange={setPng} />
          <label className="cp-check"><input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} data-testid="sign-agree" /> I agree to the terms</label>
          {needDep && <label className="cp-check"><input type="checkbox" checked={dep} onChange={(e) => setDep(e.target.checked)} data-testid="sign-authorize" /> I authorize the {usd(s.milestones.deposit)} deposit</label>}
          {error && <p className="cp-error" data-testid="sign-error">{error}</p>}
          <button className="cp-btn cp-btn--block" disabled={!ready || busy} onClick={sign} data-testid="sign-submit">{busy ? "Signing…" : "Sign agreement"}</button>
          {!ready && <p className="cp-small" style={{ margin: 0, textAlign: "center" }} data-testid="sign-missing">Still needed: {[name.trim().length < 2 && "your name", !png && "your signature", !terms && "agree to the terms", needDep && !dep && "authorize the deposit"].filter(Boolean).join(" · ")}</p>}
        </div>
      )}
    </Sec>
  );
};

export default function ClientPage() {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);
  const [poll, setPoll] = useState(null);
  const load = useCallback(() => call(`/${token}`).then(setD).catch((e) => setError(e.message)), [token]);
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow";
    document.head.appendChild(m);
    document.title = "Your hausA project";
    return () => { m.remove(); };
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const rid = params.get("canceled");
    if (rid && rid !== "1") call(`/${token}/payment-requests/${rid}/canceled`, { method: "POST" }).catch(() => {});
  }, [params, token]);
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return undefined;
    let n = 0;
    setPoll("checking");
    const t = setInterval(async () => {
      n += 1;
      try {
        const s = await call(`/${token}/payments/${sid}`);
        if (s.status === "paid" || s.status === "failed" || n > 15) { clearInterval(t); setPoll(s.status === "paid" ? "paid" : s.status === "failed" ? "failed" : "pending"); load(); if (s.status === "paid") setParams({}, { replace: true }); }
      } catch { clearInterval(t); setPoll("error"); }
    }, 2000);
    return () => clearInterval(t);
  }, [params, token, load, setParams]);
  const pay = async (body) => {
    setPaying(true);
    try { const r = await call(`/${token}/checkout`, { method: "POST", body: JSON.stringify({ ...body, origin_url: window.location.origin }) }); window.location.href = r.url; }
    catch (e) { setError(e.message); setPaying(false); }
  };

  if (error && !d) return <div className="st cp"><div className="cp-wrap"><p className="cp-brand">hausA</p><h1 className="cp-h1">This link isn't available</h1><p className="cp-p" data-testid="client-error">{error}</p></div></div>;
  if (!d) return <div className="st cp"><div className="cp-wrap"><p className="cp-p">Loading…</p></div></div>;
  const a = d.agreement;
  const signed = d.signed_agreements.length > 0;
  const open = d.requests.filter((r) => r.milestone !== "deposit" && ["pending", "failed"].includes(r.status));
  const depReq = d.requests.find((r) => r.milestone === "deposit" && ["pending", "failed"].includes(r.status));
  const allDone = signed && d.deposit.paid;
  return (
    <div className="st cp" data-testid="client-page">
      <div className="cp-wrap">
        <header className="cp-head">{d.company.logo_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${d.company.logo_url}`} alt="hausA" style={{ height: 40 }} data-testid="client-logo" /> : <p className="cp-brand">hausA</p>}<p className="cp-sub">INTERIOR DESIGN STUDIO</p></header>
        {d.test_mode && <div className="st-testmode" data-testid="client-test-banner">TEST MODE — no real money is charged</div>}
        <p className="cp-eb">Prepared for {d.client.full_name}</p>
        <h1 className="cp-h1" data-testid="client-project-name">{d.project.name}</h1>
        {poll === "checking" && <p className="cp-card" data-testid="payment-checking">Confirming your payment…</p>}
        {params.get("canceled") && <p className="cp-card" data-testid="payment-canceled">Checkout was canceled. Nothing was charged. You can try again below.</p>}
        {poll === "failed" && <p className="cp-card cp-error" data-testid="payment-failed">The payment didn't go through (the card was declined or the payment failed). Nothing was charged. Please try again or use another card.</p>}
        {poll === "pending" && <p className="cp-card" data-testid="payment-pending">We haven't received confirmation yet. If you completed checkout, refresh in a minute.</p>}
        {d.branding?.client_intro && <p className="cp-lead">{d.branding.client_intro}</p>}
        <ClientProgress d={d} />
        {(poll === "paid" || (allDone && d.status === "Ready for Design")) && (
          <section className="cp-sec cp-confirm" data-testid="client-confirmation">
            <p className="cp-eb">You're all set</p><h2 className="cp-h2">Thank you, {d.client.first_name}</h2>
            <p className="cp-p">Your agreement is signed and your deposit is received{d.test_mode ? " (test payment, no real money)" : ""}. Next, we confirm any outstanding measurements and begin your initial concept mood board. We'll be in touch within two business days.</p>
          </section>
        )}
        {d.recap ? <Recap r={d.recap} /> : <Sec eyebrow="Proposal" title="Your proposal is being prepared"><p className="cp-p">We're preparing your proposal from our consultation. It will appear here.</p></Sec>}
        {a && <Agreement a={a} token={token} clientName={d.client.full_name} onSigned={load} />}
        <ConceptBoards boards={d.concept_boards} token={token} onDone={load} />
        {d.product_board && <ProductBoard b={d.product_board} token={token} onDone={load} />}
        <OrderSection d={d} token={token} onDone={load} />
        <Services list={d.services} />
        {signed && !d.deposit.paid && (
          <Sec eyebrow="Deposit" title={`Pay your ${usd(d.deposit.amount_cents)} deposit`} testId="client-deposit">
            <p className="cp-p">The deposit reserves your design start and is credited toward your design fee. You'll pay securely with Stripe.</p>
            {depReq?.failure && <p className="cp-error" data-testid="deposit-failure">Last attempt: {depReq.failure}. Please try again.</p>}
            <button className="cp-btn" disabled={paying} onClick={() => pay({ milestone: "deposit" })} data-testid="pay-deposit">{paying ? "Opening checkout…" : `Pay ${usd(d.deposit.amount_cents)} deposit`}</button>
          </Sec>
        )}
        {open.map((r) => (
          <Sec key={r.id} eyebrow="Payment request" title={`${r.label} · ${usd(r.amount_cents)}`} testId={`client-request-${r.milestone}`}>
            <p className="cp-p">{r.milestone === "extra" ? "Optional service or additional work, separate from your design fee." : d.final_balance_note}</p>
            {r.failure && <p className="cp-error" data-testid={`request-${r.id}-failure`}>Last attempt: {r.failure}. Please try again.</p>}
            {r.last_canceled_at && <p className="cp-small">Checkout was canceled on {fmt(r.last_canceled_at)}; nothing was charged.</p>}
            <button className="cp-btn" disabled={paying} onClick={() => pay({ request_id: r.id })} data-testid={`pay-${r.milestone === "extra" ? r.id : r.milestone}`}>{paying ? "Opening checkout…" : `Pay ${usd(r.amount_cents)}`}</button>
          </Sec>
        ))}
        {d.payments.length > 0 && (
          <Sec eyebrow="Payments" title="Receipts" testId="client-payments">
            <BalanceCard b={d.balances} />
            {d.payments.map((p) => <div key={p.id} className="cp-card"><Row k={`${p.label} · ${fmt(p.paid_at)}${p.method !== "stripe" ? ` · ${p.method.toUpperCase()}` : ""}`} v={`${usd(p.amount_cents)} · ${p.status}${p.test_mode ? " (test)" : ""}`} />
              <a className="cp-link" href={`${API}/${token}/receipts/${p.id}.pdf`} target="_blank" rel="noreferrer" data-testid={`client-receipt-${p.id}`}>hausA receipt (PDF)</a>{p.receipt_url && <> · <a className="cp-link" href={p.receipt_url} target="_blank" rel="noreferrer">Stripe receipt</a></>}</div>)}
          </Sec>
        )}
        <Sec eyebrow="Documents" title="Downloads" testId="client-downloads"><Documents docs={d.documents} /></Sec>
        <footer className="cp-foot">{d.company.name}{d.company.email ? ` · ${d.company.email}` : ""}{d.company.phone ? ` · ${d.company.phone}` : ""}<br />{d.company.legal_name} · {d.company.department_line}</footer>
      </div>
    </div>
  );
}
